import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { faArchive, faStar, faTasks } from '@fortawesome/free-solid-svg-icons';
import { OutcomeTaskService, InsightsService, sharedApiClient } from '../fetcher';
import {
  IdentityService,
  TeamService,
  getAssigneeName as resolveAssigneeName,
  UserAbility,
  useNotifications,
  useAuth,
  createCoalescer,
  isAbortError,
} from '@medprompt/shared';
import {
  TaskViewMode,
  mergeFetchedTask,
  shouldFetchTaskUpdate,
  shouldRefreshTaskSearch,
  taskBelongsInView,
} from './taskListMembership';

export { TaskViewMode };

const VIEW_MODE_STORAGE_KEY = 'outcomeTaskViewMode';

const sortTasks = (tasks) => {
  if (!Array.isArray(tasks)) return [];

  return [...tasks].sort((a, b) => {
    const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return aTime - bTime;
  });
};

const useDropdown = (initialValue) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(initialValue);
  const dropdownRef = useRef(null);

  const toggle = (otherDropdowns) => {
    setIsOpen(!isOpen);
    otherDropdowns.forEach(dropdown => dropdown.setIsOpen(false));
  };

  const handleSelect = (option) => {
    setSelected(option);
    setIsOpen(false);
  };

  return {
    isOpen,
    setIsOpen,
    selected,
    setSelected,
    toggle,
    handleSelect,
    dropdownRef
  };
};

const useOutcomeTaskList = (highlightRowsRef) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamsReady, setTeamsReady] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  const [viewMode, setViewMode] = useState(() => {
    const stored = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored || TaskViewMode.MY_TASKS;
  });

  const viewModeDropdown = useDropdown('My Tasks');

  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clearSearchBarFunction, setClearSearchBarFunction] = useState(null);

  const {
    addNotification,
    addTaskUpdateCallback,
    addDocumentUpdateCallback,
    addConsultationUpdateCallback,
    addReconnectCallback,
  } = useNotifications();
  const listAbortRef = useRef(null);
  const patchAbortRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchRefreshAbortRef = useRef(null);
  const rerunSearchRef = useRef(() => {});
  const patchIdsRef = useRef(new Set());
  const outcomeTaskManager = useMemo(() => new OutcomeTaskService(sharedApiClient), []);
  const identityManager = useMemo(() => new IdentityService(), []);
  const insightsManager = useMemo(() => new InsightsService(sharedApiClient), []);

  const highlightRows = (taskIds) => {
    highlightRowsRef.current?.(new Set(taskIds));
  };

  useEffect(() => {
    const highlightTaskId = location.state?.highlightTaskId;
    if (highlightTaskId) {
      highlightRows([highlightTaskId]);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // The task may not have loaded yet when the intent arrives, so this has to wait for the list.
  // `window.history.replaceState` does not clear the router's `location.state`, so without a
  // consume-once marker every subsequent list update re-opened the modal.
  const consumedTaskOpenIntentRef = useRef(null);
  useEffect(() => {
    const taskIdToOpen = location.state?.taskId;
    if (!taskIdToOpen || consumedTaskOpenIntentRef.current === taskIdToOpen) return;

    const taskToOpen = tasks.find(task => task.taskId === taskIdToOpen);
    if (!taskToOpen) return;

    consumedTaskOpenIntentRef.current = taskIdToOpen;
    setSelectedTask(taskToOpen);
    setModalOpen(true);
    highlightRows([taskIdToOpen]);
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, tasks]);

  useEffect(() => {
    sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  const selectedTaskRef = useRef(selectedTask);
  selectedTaskRef.current = selectedTask;

  // No "already fetching" guard: it would drop the newest request while keeping the one the abort
  // below has just cancelled, leaving the list permanently empty. Newest request wins.
  const fetchTasks = useCallback(async () => {
    const isInitialLoad = tasksRef.current.length === 0;
    if (isInitialLoad) {
      setLoading(true);
    }

    patchIdsRef.current.clear();
    patchAbortRef.current?.abort();
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    const options = { signal: controller.signal };

    try {
      const hasTaskRead = user?.authorities?.includes(UserAbility.TASK_READ.key);
      let data;

      switch (viewMode) {
        case TaskViewMode.MY_TASKS:
          data = await outcomeTaskManager.fetchYourTasks(options);
          break;

        case TaskViewMode.ALL_TASKS:
          data = hasTaskRead
            ? await outcomeTaskManager.fetchActiveTasks(options)
            : await outcomeTaskManager.fetchYourTasks(options);
          break;

        case TaskViewMode.ARCHIVE:
          data = await outcomeTaskManager.fetchArchiveTasks(options);
          break;

        default:
          console.warn(`Unknown view mode: ${viewMode}, defaulting to MY_TASKS`);
          data = await outcomeTaskManager.fetchYourTasks(options);
      }

      if (listAbortRef.current !== controller) {
        return;
      }
      setTasks(Array.isArray(data) ? data : []);
      rerunSearchRef.current();
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      console.error('Failed to fetch outcome tasks', error);
      addNotification('fetch-tasks-error', 'Failed to fetch outcome tasks', { isError: true });
    } finally {
      if (listAbortRef.current === controller) {
        listAbortRef.current = null;
        if (isInitialLoad) {
          setLoading(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, viewMode]);

  const fetchActiveUsers = async () => {
    try {
      const data = await identityManager.fetchActiveClinicUsers();
      setActiveUsers(data || []);
    } catch (error) {
      console.error('Failed to fetch active users', error);
      addNotification('fetch-active-users-error', 'Failed to fetch active users', { isError: true });
    }
  };

  const fetchTeams = async () => {
    try {
      const teamManager = new TeamService();
      const data = await teamManager.fetchTeams();
      setTeams(data || []);
      setTeamsReady(true);
    } catch (error) {
      console.error('Failed to fetch teams', error);
      addNotification('fetch-teams-error', 'Failed to fetch teams', { isError: true });
    }
  };

  useEffect(() => {
    fetchActiveUsers();
    fetchTeams();
    return () => {
      listAbortRef.current?.abort();
      patchAbortRef.current?.abort();
      searchAbortRef.current?.abort();
      searchRefreshAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [viewMode, fetchTasks]);

  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;

  const hasTaskRead = Boolean(user?.authorities?.includes(UserAbility.TASK_READ.key));
  const listSyncRef = useRef({
    tasks,
    viewMode,
    userId: null,
    teams,
    teamsReady,
    hasTaskRead,
    searchResults,
    isSearchActive,
    searchQuery,
  });
  listSyncRef.current = {
    tasks,
    viewMode,
    userId: user?.userId ?? null,
    teams,
    teamsReady,
    hasTaskRead,
    searchResults,
    isSearchActive,
    searchQuery,
  };

  const patchTasks = useCallback(async (taskIds) => {
    patchAbortRef.current?.abort();
    const controller = new AbortController();
    patchAbortRef.current = controller;

    const results = await Promise.all(taskIds.map(async (taskId) => {
      try {
        return await outcomeTaskManager.fetchOutcomeTask(taskId, { signal: controller.signal });
      } catch (error) {
        return isAbortError(error) ? undefined : null;
      }
    }));

    if (controller.signal.aborted) return;
    if (patchAbortRef.current === controller) {
      patchAbortRef.current = null;
    }

    const { viewMode: mode, userId, teams: memberTeams, teamsReady: ready, hasTaskRead: canReadAll, tasks: shown } = listSyncRef.current;
    const context = { userId, teams: memberTeams, teamsReady: ready, hasTaskRead: canReadAll };
    const decisions = taskIds.map((taskId, index) => {
      const fetched = results[index];
      if (fetched === undefined) return { taskId, skip: true };
      const belongs = fetched == null ? false : taskBelongsInView(fetched, mode, context);
      return { taskId, fetched, belongs, skip: false };
    });

    setTasks(previous => {
      let next = previous;
      decisions.forEach((decision) => {
        if (decision.skip) return;
        next = mergeFetchedTask(next, decision.taskId, decision.fetched, decision.belongs);
      });
      return next;
    });

    decisions.forEach((decision) => {
      if (decision.skip) return;
      const stays = decision.belongs === true
        || (decision.belongs == null && shown.some(task => task.taskId === decision.taskId));
      if (!stays) patchIdsRef.current.delete(decision.taskId);
    });
  }, [outcomeTaskManager]);

  const patchHandlerRef = useRef(patchTasks);
  useEffect(() => {
    patchHandlerRef.current = patchTasks;
  }, [patchTasks]);

  useEffect(() => {
    const ids = patchIdsRef.current;
    if (ids.size === 0) return;
    for (const id of ids) {
      if (tasks.some(task => task.taskId === id)) ids.delete(id);
    }
  }, [tasks]);

  const patchCoalescerRef = useRef(null);
  const searchCoalescerRef = useRef(null);
  useEffect(() => {
    patchCoalescerRef.current = createCoalescer((ids) => patchHandlerRef.current(ids));
    searchCoalescerRef.current = createCoalescer(() => rerunSearchRef.current());
    return () => {
      patchCoalescerRef.current?.cancel();
      searchCoalescerRef.current?.cancel();
    };
  }, []);

  useEffect(() => {
    if (!addTaskUpdateCallback) return undefined;

    const handleTaskUpdate = (taskId, statusTransition) => {
      if (!taskId) return;
      if (statusTransition?.to === 'NON_EXISTENT') {
        patchIdsRef.current.delete(taskId);
        if (selectedTaskRef.current?.taskId === taskId) {
          setModalOpen(false);
          setSelectedTask(null);
        }
        setTasks(previous => previous.filter(task => task.taskId !== taskId));
        setSearchResults(previous => previous.filter(task => task.taskId !== taskId));
        return;
      }
      const { tasks: shown, viewMode: mode, hasTaskRead: canReadAll, searchResults: results, isSearchActive: searching } = listSyncRef.current;
      const isTracked = shown.some(task => task.taskId === taskId) || patchIdsRef.current.has(taskId);
      if (shouldFetchTaskUpdate(isTracked, statusTransition, mode, canReadAll)) {
        patchIdsRef.current.add(taskId);
        patchCoalescerRef.current?.push(taskId);
      }
      const inSearch = results.some(task => task.taskId === taskId);
      if (searching && shouldRefreshTaskSearch(inSearch, statusTransition)) {
        searchCoalescerRef.current?.push(taskId);
      }
    };

    return addTaskUpdateCallback(handleTaskUpdate);
  }, [addTaskUpdateCallback]);

  // Filing a document or consultation is what creates tasks. Those events carry no task id.
  useEffect(() => {
    const onFiledAway = (_id, statusTransition) => {
      if (statusTransition?.to === 'FILED_AWAY') {
        fetchTasksRef.current();
      }
    };
    const unsubDocument = addDocumentUpdateCallback?.(onFiledAway);
    const unsubConsultation = addConsultationUpdateCallback?.(onFiledAway);
    return () => {
      unsubDocument?.();
      unsubConsultation?.();
    };
  }, [addDocumentUpdateCallback, addConsultationUpdateCallback]);

  useEffect(() => {
    if (!addReconnectCallback) return;
    return addReconnectCallback(() => fetchTasksRef.current());
  }, [addReconnectCallback]);

  const recordTaskOpened = async (taskId) => {
    try {
      await insightsManager.recordTaskOpened(taskId);
    } catch (error) {
      console.error('Failed to record task opened event:', error);
    }
  };

  const openTask = (task) => {
    recordTaskOpened(task.taskId);
    setSelectedTask(task);
    setModalOpen(true);
  };

  const closeTask = () => {
    setModalOpen(false);
    setSelectedTask(null);
  };

  const taskFieldsFromUpdate = ({ taskId, isUrgent, isNotUrgent, ...fields }) => {
    const patch = { ...fields };
    if (isUrgent) patch.isUrgent = true;
    if (isNotUrgent) patch.isUrgent = false;
    return patch;
  };

  // Both the list and the search results feed the table, so a patch has to reach both.
  // Deliberately does not touch `selectedTask`.
  const applyTaskPatch = useCallback((taskId, fields) => {
    const patchMatching = (collection) => collection.map(task => (
      task.taskId === taskId ? { ...task, ...fields } : task
    ));
    setTasks(patchMatching);
    setSearchResults(patchMatching);
  }, []);

  const removeTaskLocally = useCallback((taskId) => {
    const withoutTask = (collection) => collection.filter(task => task.taskId !== taskId);
    setTasks(withoutTask);
    setSearchResults(withoutTask);
  }, []);

  const handleSaveTask = async (taskUpdate) => {
    setModalLoading(true);
    try {
      await outcomeTaskManager.updateOutcomeTask(taskUpdate);
      applyTaskPatch(taskUpdate.taskId, taskFieldsFromUpdate(taskUpdate));
      setModalLoading(false);
    } catch (error) {
      console.error('Failed to update task', error);
      setModalLoading(false);
      addNotification('task-update-error', 'Failed to update task', { isError: true });
    }
  };

  const handleMarkComplete = async (taskUpdate) => {
    setModalLoading(true);
    try {
      await outcomeTaskManager.completeOutcomeTask(taskUpdate.taskId);
      applyTaskPatch(taskUpdate.taskId, { isComplete: true });
      setModalLoading(false);
      setModalOpen(false);
    } catch (error) {
      console.error('Failed to mark task as complete', error);
      setModalLoading(false);
      addNotification('task-complete-error', 'Failed to mark task as complete', { isError: true });
    }
  };

  const handleDeleteTask = async (taskId) => {
    setModalLoading(true);
    try {
      await outcomeTaskManager.deleteOutcomeTask(taskId);
      removeTaskLocally(taskId);
      setModalLoading(false);
      setModalOpen(false);
      setSelectedTask(null);
    } catch (error) {
      console.error('Failed to delete task', error);
      setModalLoading(false);
      addNotification('task-delete-error', 'Failed to delete task', { isError: true });
    }
  };

  const rerunSearch = useCallback(async () => {
    const { isSearchActive: searching, searchQuery: query } = listSyncRef.current;
    if (!searching || !query?.trim()) return;

    searchRefreshAbortRef.current?.abort();
    const controller = new AbortController();
    searchRefreshAbortRef.current = controller;

    try {
      const results = await outcomeTaskManager.searchOutcomeTasks(query, { signal: controller.signal });
      if (searchRefreshAbortRef.current !== controller) return;
      const current = listSyncRef.current;
      if (!current.isSearchActive || current.searchQuery !== query) return;
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('Error refreshing task search:', error);
      }
    } finally {
      if (searchRefreshAbortRef.current === controller) {
        searchRefreshAbortRef.current = null;
      }
    }
  }, [outcomeTaskManager]);
  rerunSearchRef.current = rerunSearch;

  const handleTaskSearch = useCallback(async (query, callback) => {
    searchAbortRef.current?.abort();
    searchRefreshAbortRef.current?.abort();

    if (!query || !query.trim()) {
      searchAbortRef.current = null;
      setSearchResults([]);
      setIsSearchActive(false);
      setSearchQuery('');
      if (callback) {
        callback([]);
      }
      return;
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;

    try {
      const results = await outcomeTaskManager.searchOutcomeTasks(
        query, { signal: controller.signal });
      if (searchAbortRef.current !== controller) {
        return;
      }
      setSearchResults(Array.isArray(results) ? results : []);
      setIsSearchActive(true);
      setSearchQuery(query);
      if (callback) {
        callback(results);
      }
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      console.error('Error searching tasks:', error);
      setSearchResults([]);
      setIsSearchActive(false);
      setSearchQuery('');
      if (callback) {
        callback([]);
      }
    } finally {
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null;
      }
    }
  }, [outcomeTaskManager]);

  const handleClearSearch = () => {
    searchRefreshAbortRef.current?.abort();
    setSearchResults([]);
    setIsSearchActive(false);
    setSearchQuery('');
    if (clearSearchBarFunction) {
      clearSearchBarFunction();
    }
  };

  const getAssigneeName = useCallback((assigneeId) => {
    return resolveAssigneeName(assigneeId, activeUsers, teams);
  }, [activeUsers, teams]);

  const viewModeOptions = useMemo(() => ([
    { name: 'My Tasks', value: TaskViewMode.MY_TASKS, icon: faStar },
    { name: 'All Tasks', value: TaskViewMode.ALL_TASKS, icon: faTasks },
    { name: 'Archive', value: TaskViewMode.ARCHIVE, icon: faArchive }
  ]), []);

  const currentViewModeOption = viewModeOptions.find(opt => opt.value === viewMode) || viewModeOptions[0];

  const selectViewMode = (option) => {
    setViewMode(option.value);
    viewModeDropdown.handleSelect(option.name);
  };

  const sortedTasks = useMemo(() => {
    if (isSearchActive) return sortTasks(searchResults);
    const context = {
      userId: user?.userId ?? null,
      teams,
      teamsReady,
      hasTaskRead: Boolean(user?.authorities?.includes(UserAbility.TASK_READ.key)),
    };
    return sortTasks(tasks.filter(task => taskBelongsInView(task, viewMode, context) !== false));
  }, [isSearchActive, searchResults, tasks, viewMode, user, teams, teamsReady]);

  return {
    outcomeTaskManager,
    addNotification,
    applyTaskPatch,
    removeTaskLocally,
    setTasks,
    handleMarkComplete,
    handleDeleteTask,
    getAssigneeName,
    activeUsers,
    teams,
    loading,
    selectedTask,
    modalOpen,
    modalLoading,
    closeTask,
    openTask,
    handleSaveTask,
    isSearchActive,
    searchResults,
    searchQuery,
    setClearSearchBarFunction,
    handleTaskSearch,
    handleClearSearch,
    viewModeDropdown,
    viewModeOptions,
    currentViewModeName: currentViewModeOption.name,
    currentViewModeIcon: currentViewModeOption.icon,
    selectViewMode,
    sortedTasks,
  };
};

export default useOutcomeTaskList;
